/**
 * PHARMAKON — Cross-module X XWP-77 (XD-228) — the one sale-price resolver of a counter line, for the SPA.
 *
 * A re-export of packages/shared/pricing/prix-vente.js (the server prices with its byte-identical twin
 * apps/server/src/utils/pricing/prix-vente.js): the confirmed secours price > 0, else the lot's
 * `prix_vente_ppa` > 0, else `Produit.ppa` > 0, else unknown — a price ≤ 0 counts as absent. A counter
 * component never re-derives a price with a local chain on `ppa` / `prix_vente_ppa` (XMD-CP-28): it reads
 * the server's `prix_vente` (search row) / `prix_unitaire` (window line), or calls this resolver for an
 * explicit lot.
 *
 * X XWP-80 — the two readers every counter screen uses (guarded by lib/__tests__/prix-lecteurs.guard.test.js):
 *   · `prixUnitaireDA` — a line priced on its lot (Vente CHIFA, Instance à terme): the resolver, in DA;
 *   · `prixDeLigneRecherche` — a `GET /produits/search` row: the server's `prix_vente`, as sent.
 */
import { resoudrePrixVente } from "@pharmakon/shared/pricing/prix-vente.js";
import { versDinars } from "@pharmakon/shared/pricing/money.js";

export {
  SOURCE_PRIX_VENTE,
  prixPositifCentimes,
  resoudrePrixVente,
  ppaProduitSecondaire,
  prixVenteAffiche,
} from "@pharmakon/shared/pricing/prix-vente.js";

/**
 * The unit price (DA, number) a counter line is charged on its lot by the one resolver — null when
 * nothing prices it (the line shows « — » / « Prix inconnu », never 0 DA).
 * @param {{ lot?: { prix_vente_ppa?: * }|null, produit?: { ppa?: * }|null, secours?: { prix_confirme?: * }|null }} [p]
 * @returns {number|null}
 */
export function prixUnitaireDA({ lot = null, produit = null, secours = null } = {}) {
  const r = resoudrePrixVente({ lot, produit, secours });
  return r.centimes === null ? null : versDinars(r.centimes);
}

const AUCUN_PRIX = Object.freeze({ montant: null, source: null, lot_id: null, ppa_produit: null });

/**
 * The price of a counter search row: the server's `prix_vente { montant, source, lot_id, ppa_produit }`
 * (XWP-77 — the FEFO head priced by the resolver, `ppa_produit` = Produit.ppa when it differs). A row
 * without it is unknown: the SPA never re-derives it from `Produit.ppa` or a lot of the row.
 * @param {{ prix_vente?: object|null }|null} row
 * @returns {{ montant: string|null, source: string|null, lot_id: string|null, ppa_produit: string|null }}
 */
export function prixDeLigneRecherche(row) {
  const pv = row?.prix_vente;
  if (!pv || typeof pv !== "object") return { ...AUCUN_PRIX };
  return {
    montant: pv.montant ?? null,
    source: pv.source ?? null,
    lot_id: pv.lot_id ?? null,
    ppa_produit: pv.ppa_produit ?? null,
  };
}
